import { useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { type BookPdfSlot } from '@services/firebase/book-pdf-reader'
import { type FlipbookAnnotationRepository } from '@services/firebase/flipbook-annotations'
import { AppStatusState, LmsButton, TooltipProvider } from '@shared/ui'
import { FlipbookProgress } from './flipbook/FlipbookProgress'
import { type FlipbookAnnotationColorTool, type FlipbookAnnotationTool } from './flipbook/FlipbookAnnotationLayer'
import { EMPTY_FLIPBOOK_ANNOTATION_HISTORY, type FlipbookAnnotationHistoryControls } from './flipbook/flipbook-annotation-contracts'
import { FlipbookStage } from './flipbook/FlipbookStage'
import { FlipbookToolbar } from './flipbook/FlipbookToolbar'
import { useFlipbookNavigation } from './flipbook/useFlipbookNavigation'
import { useFlipbookAnnotationPersistence } from './flipbook/useFlipbookAnnotationPersistence'
import { useFlipbookViewport } from './flipbook/useFlipbookViewport'
import { useProtectedPageResources } from './flipbook/useProtectedPageResources'
import { useStudyTimeTracker } from '@features/learning/shared/useStudyTimeTracker'
import { type ProtectedPageSource } from './flipbook/flipbook-contracts'
import { useFlipbookFirstOpenDiagnosticsText, useFlipbookPerformanceRenderCount, useFlipbookPerformanceRun } from './flipbook/flipbook-performance-diagnostics'
import './flipbook/flipbook.css'

const DEFAULT_PEN_COLOR = 'var(--lms-color-brand-accent)'
const DEFAULT_HIGHLIGHTER_COLOR = 'var(--lms-color-brand-primary)'
const DEFAULT_TEXT_COLOR = 'var(--lms-flipbook-annotation-ink)'

interface PdfFlipbookReaderProps {
  readonly bookId: string | null
  readonly bookTitle: string
  readonly onBack: () => void
  readonly slot: BookPdfSlot
  /** Test harness injection only; normal application routes leave this undefined. */
  readonly testOnlyOverrides?: PdfFlipbookReaderTestOverrides
}

export interface PdfFlipbookReaderTestControls {
  readonly reloadVisiblePage: () => Promise<void>
  readonly retryAnnotationSync: () => void
}

export interface PdfFlipbookReaderTestOverrides {
  readonly annotationRepository?: FlipbookAnnotationRepository
  readonly onTestOnlyControlsReady?: (controls: PdfFlipbookReaderTestControls) => void
  readonly protectedPageSource?: ProtectedPageSource<BookPdfSlot>
}

function getSlotLabel(slot: BookPdfSlot) {
  return slot === 'workbookPdf' ? 'Workbook' : 'Book'
}

export function PdfFlipbookReader({
  bookId,
  bookTitle,
  onBack,
  slot,
  testOnlyOverrides,
}: PdfFlipbookReaderProps) {
  useFlipbookPerformanceRun()
  useFlipbookPerformanceRenderCount('reader')
  useStudyTimeTracker({
    enabled: !testOnlyOverrides,
    surface: 'pdf-flipbook',
  })
  const firstOpenDiagnosticsText = useFlipbookFirstOpenDiagnosticsText()
  const [interactionErrorMessage, setInteractionErrorMessage] = useState<string | null>(null)
  const [annotationTool, setAnnotationTool] = useState<FlipbookAnnotationTool>('none')
  const [annotationHistory, setAnnotationHistory] = useState<FlipbookAnnotationHistoryControls>(EMPTY_FLIPBOOK_ANNOTATION_HISTORY)
  const [penColor, setPenColor] = useState(DEFAULT_PEN_COLOR)
  const [highlighterColor, setHighlighterColor] = useState(DEFAULT_HIGHLIGHTER_COLOR)
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR)
  const swipeHandlerRef = useRef<(deltaX: number) => void>(() => undefined)
  const {
    errorMessage: resourceErrorMessage,
    isPageLoading,
    isSessionLoading,
    loadPages,
    loadPreview,
    pageImages,
    renewSession,
    session,
    sessionResetKey,
  } = useProtectedPageResources({ bookId, slot, source: testOnlyOverrides?.protectedPageSource })
  const errorMessage = resourceErrorMessage ?? interactionErrorMessage
  const {
    bookRef,
    handlePointerCancel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleToggleFullscreen,
    isDesktopSpread,
    isFullscreen,
    isPanning,
    pan,
    readerRef,
    stageRef,
    zoom,
    zoomIn,
    zoomOut,
  } = useFlipbookViewport({
    onSwipe: (deltaX) => swipeHandlerRef.current(deltaX),
    sessionToken: session?.sessionToken ?? null,
  })
  const {
    annotationHydrationKey,
    hydratedAnnotationState,
    isAnnotationHydrationReady,
    onAnnotationStateChange,
    retryAnnotationSync,
    syncStatus: annotationSyncStatus,
  } = useFlipbookAnnotationPersistence({
    bookId,
    revision: session?.revision ?? null,
    repository: testOnlyOverrides?.annotationRepository,
    slot,
  })
  const {
    adjacentPageNumbers,
    displayPage,
    handleKeyDown,
    handleSwipe,
    isSpreadView,
    navigateByStep,
    navigateToPage,
    turningPage,
    visiblePageNumbers,
  } = useFlipbookNavigation({ isDesktopSpread, isPageLoading, pageImages, session, sessionResetKey })

  useEffect(() => {
    if (!testOnlyOverrides?.onTestOnlyControlsReady) {
      return
    }

    testOnlyOverrides.onTestOnlyControlsReady({
      reloadVisiblePage: async () => {
        await renewSession({ preserveReaderSurface: true })
        await loadPages({ prefetchPages: [], visiblePages: visiblePageNumbers })
      },
      retryAnnotationSync,
    })
  }, [loadPages, renewSession, retryAnnotationSync, testOnlyOverrides, visiblePageNumbers])

  useEffect(() => {
    swipeHandlerRef.current = handleSwipe
  }, [handleSwipe])

  useEffect(() => {
    // The resource hook owns request ordering, deduplication, caching, and URL cleanup.
    void loadPages({ prefetchPages: adjacentPageNumbers, visiblePages: visiblePageNumbers })
  }, [adjacentPageNumbers, loadPages, visiblePageNumbers])

  const handleRetry = () => {
    setInteractionErrorMessage(null)
    void renewSession()
  }

  const handleAnnotationColorChange = (tool: FlipbookAnnotationColorTool, color: string) => {
    if (tool === 'pen') {
      setPenColor(color)
      if (annotationHistory.selectedStrokeTool === 'pen') {
        annotationHistory.recolorSelectedStroke(color)
      }
      return
    }

    if (tool === 'highlighter') {
      setHighlighterColor(color)
      if (annotationHistory.selectedStrokeTool === 'highlighter') {
        annotationHistory.recolorSelectedStroke(color)
      }
      return
    }

    setTextColor(color)
    annotationHistory.recolorSelectedText(color)
  }

  const slotLabel = getSlotLabel(slot)
  const getRenderedPageImage = (page: number) => {
    if (pageImages[page]) {
      return pageImages[page]
    }

    if (!turningPage) {
      return undefined
    }

    const fallbackPage = isSpreadView
      ? page + (turningPage.direction === 'next' ? -2 : 2)
      : page + (turningPage.direction === 'next' ? -1 : 1)

    return pageImages[fallbackPage]
  }
  const getRenderedPageNumber = (page: number) => {
    if (pageImages[page]) {
      return page
    }

    if (!turningPage) {
      return page
    }

    return isSpreadView
      ? page + (turningPage.direction === 'next' ? -2 : 2)
      : page + (turningPage.direction === 'next' ? -1 : 1)
  }
  const hasVisiblePage = visiblePageNumbers.some((page) => Boolean(getRenderedPageImage(page)))
  const pageMetadata = session?.pages[displayPage - 1]
  const pageAspectRatio = session?.pages[0]?.height
    ? session.pages[0].width / session.pages[0].height
    : 0.7

  return (
    <TooltipProvider delayDuration={250}>
      <main
        className={`pdfFlipbookReader${isFullscreen ? ' isFullscreen' : ''}`}
        ref={readerRef}
      >
        <FlipbookToolbar
          bookTitle={bookTitle}
          displayPage={displayPage}
          isFullscreen={isFullscreen}
          isPageLoading={isPageLoading}
          highlighterColor={highlighterColor}
          annotationHistory={annotationHistory}
          onBack={onBack}
          onAnnotationColorChange={handleAnnotationColorChange}
          onAnnotationToolChange={setAnnotationTool}
          onNextPage={() => { navigateByStep('next') }}
          onNavigateToPage={navigateToPage}
          onPreviousPage={() => { navigateByStep('previous') }}
          onToggleFullscreen={() => { void handleToggleFullscreen() }}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          pageCount={session?.pageCount ?? 0}
          pageNavigationKey={JSON.stringify([bookId, slot, sessionResetKey])}
          visiblePageNumbers={visiblePageNumbers}
          progress={session ? (
            <FlipbookProgress
              displayPage={displayPage}
              isDesktopSpread={isDesktopSpread}
              loadPreview={loadPreview}
              onNavigateToPage={navigateToPage}
              pageAspectRatio={pageAspectRatio}
              pageImages={pageImages}
              session={session}
              visiblePageNumbers={visiblePageNumbers}
            />
          ) : null}
          annotationTool={annotationTool}
          annotationSyncStatus={annotationSyncStatus}
          penColor={penColor}
          selectedTextColor={annotationHistory.selectedTextColor}
          textColor={textColor}
          zoom={zoom}
        />
        <div className={`pdfFlipbookReaderContent${isFullscreen ? ' isFullscreen' : ''}`}>
          {isSessionLoading ? (
            <AppStatusState isLoading minHeight="min(68vh, 42rem)" message="Preparing the protected reader." title="Loading PDF" />
          ) : null}

          {!isSessionLoading && errorMessage && !hasVisiblePage && !session ? (
            <div className="pdfFlipbookReaderError">
              <AppStatusState tone="error" message={errorMessage} title="PDF is unavailable" />
              <LmsButton className="mx-auto min-h-11" leadingIcon={<RefreshCw aria-hidden="true" className="size-4" />} onClick={handleRetry} variant="outline">
                Retry
              </LmsButton>
            </div>
          ) : null}

          {!isSessionLoading && session ? (
            <div className="pdfFlipbookSession">
              <FlipbookStage
                bookRef={bookRef}
                displayPage={displayPage}
                errorMessage={errorMessage}
                annotationTool={annotationTool}
                annotationHydrationKey={annotationHydrationKey}
                hydratedAnnotationState={hydratedAnnotationState}
                highlighterColor={highlighterColor}
                getRenderedPageImage={getRenderedPageImage}
                getRenderedPageNumber={getRenderedPageNumber}
                hasVisiblePage={hasVisiblePage}
                isFullscreen={isFullscreen}
                isPageLoading={isPageLoading}
                isPanning={isPanning}
                isSpreadView={isSpreadView}
                isAnnotationHydrationReady={isAnnotationHydrationReady}
                onAnnotationHistoryChange={setAnnotationHistory}
                onAnnotationStateChange={onAnnotationStateChange}
                onKeyDown={handleKeyDown}
                onPageImageError={() => { setInteractionErrorMessage('This page could not be displayed. Renew the reader and try again.') }}
                onPointerCancel={handlePointerCancel}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onRetry={handleRetry}
                pageAspectRatio={pageAspectRatio}
                pan={pan}
                penColor={penColor}
                session={session}
                slotLabel={slotLabel}
                stageRef={stageRef}
                textColor={textColor}
                turningPage={turningPage}
                visiblePageNumbers={visiblePageNumbers}
                zoom={zoom}
              />
              {errorMessage && hasVisiblePage ? (
                <div className="pdfFlipbookInlineError">
                  <p role="alert">
                    {errorMessage}
                  </p>
                  <LmsButton className="min-h-11" leadingIcon={<RefreshCw aria-hidden="true" className="size-4" />} onClick={handleRetry} variant="outline">
                    Renew reader
                  </LmsButton>
                </div>
              ) : null}
              {pageMetadata ? <span className="sr-only">Page size {pageMetadata.width} by {pageMetadata.height}.</span> : null}
              {firstOpenDiagnosticsText ? (
                <span className="sr-only" data-lms-flipbook-first-open-diagnostics="true">
                  {firstOpenDiagnosticsText}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </TooltipProvider>
  )
}
